import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ContextMenu, { ContextMenuItem, ContextMenuLabel } from './ContextMenu.jsx'
import { monthDays, weekdayLabels } from './Calendar.jsx'
import { formatDate, windowLabel } from '../lib/dates.js'
import { reasonLabel } from '../../../shared/timeoff.js'
import { conflictsOn } from '../lib/conflicts.js'


/* One entry line, and the gap under it. Used to work out how many will fit in a cell before
   any of them are drawn — see useVisibleCount. Kept in step with .mcal-off in styles.css. */
const ITEM_H = 17

/* A title attribute renders one entry per line only if the newlines are real ones. */
const NEWLINE = String.fromCharCode(10)
const HEAD_H = 22

/**
 * How many entries fit in a cell, measured rather than assumed.
 *
 * The cell's height comes from the viewport (see .mcal-day), so there is no fixed number to
 * hard-code: the same month shows six entries on a large monitor and three on a laptop. This
 * measures one real cell and converts it into a count, and every cell uses that count —
 * they are all the same height, because the grid makes them so.
 */
function useVisibleCount(gridRef) {
  const [max, setMax] = useState(4)

  const measure = useCallback(() => {
    const cell = gridRef.current?.querySelector('.mcal-day')
    if (!cell) return
    const room = cell.getBoundingClientRect().height - HEAD_H
    setMax(Math.max(1, Math.floor(room / ITEM_H)))
  }, [gridRef])

  useLayoutEffect(measure, [measure])
  useEffect(() => {
    const cell = gridRef.current?.querySelector('.mcal-day')
    if (!cell || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(cell)
    return () => ro.disconnect()
  }, [gridRef, measure])

  return max
}

/**
 * A month of the board at once — who is working, and who is away.
 *
 * A month cannot show the board the way Day and Week do. Thirty-odd staff against a job each
 * is a grid a day wide, and no arrangement of it fits a cell four centimetres across. So a
 * day here is a list of names: who is booked, and who is off. The detail of WHAT each person
 * is doing stays on the day it belongs to, one click away through the date.
 *
 * That also decides what the view cannot do. Notify sends the schedule for what is on
 * screen, and a list of names is not a board anyone could work a shift from — so it is off.
 * Collapsing is per team, and there are no team rows here to collapse. Printing is on,
 * because a month is exactly the kind of thing that ends up on a wall.
 */
export default function MonthGrid({
  board,
  month,
  weekStart = 1,
  today,
  staffById,
  staffFilter = null,
  onOpenDay,
  onAddDay,
  onEditTimeOff,
  readOnly = false,
}) {
  const gridRef = useRef(null)
  const maxItems = useVisibleCount(gridRef)
  const [menu, setMenu] = useState(null) // { x, y, date } | null

  // Shared with the picker's calendar, so "is this an outside day" is answered once.
  const days = monthDays(month, weekStart, today)
  const labels = weekdayLabels(month, weekStart)

  // A null filter means everyone — not an empty set, so "narrowed to nobody" stays
  // impossible to reach by accident.
  const inScope = (staffId) => !staffFilter || staffFilter.has(staffId)

  const timeOff = (board?.timeOff || []).filter((t) => inScope(t.staffId))
  const offOn = (d) => timeOff.filter((t) => t.from <= d && d <= t.to)

  const jobTypes = new Map((board?.jobTypes || []).map((j) => [j.id, j.name]))
  const jobTypeName = (id) => jobTypes.get(id)

  // Who is booked on each day, and onto what. A person with three jobs on one day is one
  // name, not three — this view answers "is Amanda working on the 12th" — but the jobs are
  // kept against the name so hovering it can say what they actually are without a fetch.
  const workingOn = new Map()
  for (const a of board?.assignments || []) {
    if (!inScope(a.staffId)) continue
    let day = workingOn.get(a.date)
    if (!day) workingOn.set(a.date, (day = new Map()))
    const jobs = day.get(a.staffId) || []
    // "Task | type" — the job someone is on and what kind of work it is are different
    // questions, and the name alone answers neither reliably: a label is free text, and a
    // type without one says what but never which.
    const label = a.label?.trim() || 'Untitled'
    const type = jobTypeName(a.jobTypeId)
    // The scheduler's own note rides along on the same line, for the same reason it shows on
    // the day and week boards: it is the one thing on a job that cannot be guessed from it.
    const note = a.internalNote?.trim()
    const base = type ? `${label} | ${type}` : label
    jobs.push(note ? `${base}${String.fromCharCode(32, 8212, 32)}${note}` : base)
    day.set(a.staffId, jobs)
  }

  // The red ring, from the one rule that decides what a conflict is (lib/conflicts.js) —
  // rather than from a second copy of it written here, which is what this was. Keyed by day
  // and person, because the ring belongs to the absence line: one mark whether the day holds
  // one job or three. Settled pairs are not in the list, so saying "we know" in the panel
  // takes the ring off here too.
  const clashes = new Set()
  for (const c of conflictsOn(board)) {
    if (c.kind !== 'conflict' || c.resolved || !inScope(c.assignment.staffId)) continue
    clashes.add(`${c.date}:${c.assignment.staffId}`)
  }

  const canEdit = !readOnly && Boolean(onEditTimeOff)
  const canOpen = !readOnly && Boolean(onOpenDay)
  const canAdd = !readOnly && Boolean(onAddDay)
  const nameOf = (id) => staffById?.get(id)?.name || 'Someone'
  // The month view asks for whole weeks, so this covers the outside-month cells too and
  // they get badged like any other day.
  const holidayOn = new Map((board?.days || []).map((x) => [x.date, x.holiday]))

  return (
    <div className="card mcal">
      <div className="mcal-grid" ref={gridRef}>
        {labels.map((l) => (
          <div key={l} className="mcal-label">{l}</div>
        ))}

        {days.map(({ date: d, outside, weekend, isToday }) => {
          const off = offOn(d)
          const offIds = new Set(off.map((t) => t.staffId))
          // Somebody booked on a day they are also off is a real conflict, and it is worth
          // seeing — but as one marked entry, not as their name twice in two colours, which
          // reads as a rendering fault. The absence is the line that stays; it carries the
          // conflict, and the day view has the booking itself.
          const working = [...(workingOn.get(d)?.entries() || [])]
            .filter(([id]) => !offIds.has(id))
            .map(([id, jobs]) => ({ id, name: nameOf(id), jobs }))
            .sort((a, b) => a.name.localeCompare(b.name))
          const holiday = holidayOn.get(d) || null

          // Away first, so the exception stays on screen when a busy day has to truncate:
          // "everyone is in" is the assumption, and the absences are the news.
          const items = [
            ...off.map((t) => ({
              kind: 'off',
              key: t.id,
              name: nameOf(t.staffId),
              entry: t,
              clash: clashes.has(`${d}:${t.staffId}`),
            })),
            ...working.map((w) => ({ kind: 'on', key: `on-${w.id}`, name: w.name, jobs: w.jobs })),
          ]
          // The overflow line costs a row of its own, so it only earns its place when it
          // hides more than the one entry it would displace.
          const overflow = items.length > maxItems ? items.length - (maxItems - 1) : 0
          const visible = overflow ? items.slice(0, maxItems - 1) : items

          return (
            <div
              key={d}
              className={[
                'mcal-day',
                outside ? 'outside' : '',
                weekend ? 'weekend' : '',
                holiday?.observed ? 'holiday' : '',
                isToday ? 'today' : '',
                canOpen ? 'openable' : '',
              ].filter(Boolean).join(' ')}
              // The whole cell opens the day — the names on it included, so there is one
              // target rather than a patchwork of them. The single exception is a time-off
              // entry, which stops the click itself because it means that absence.
              onClick={canOpen ? () => onOpenDay(d) : undefined}
              onContextMenu={
                canAdd
                  ? (e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, date: d }) }
                  : undefined
              }
              title={canOpen ? 'Click to open this day · right-click to book time off' : undefined}
            >
              <div className="mcal-day-head">
                <span className="mcal-date">{Number(d.slice(8))}</span>
                {holiday?.observed && (
                  <span className="mcal-stat" title={holiday.name}>STAT</span>
                )}
              </div>

              {/* Each entry is one person, repeated on every day their absence covers — a bar
                  spanning the run would be tidier and far worse to read, because the name
                  would sit in one cell and the days either side of it would be blank. */}
              <div className="mcal-items">
                {visible.map((it) =>
                  it.kind === 'off' ? (
                    canEdit ? (
                      <button
                        key={it.key}
                        type="button"
                        // Amber and the reason only on the clickable item, as on the other
                        // grids (OffMarker.jsx): the printout draws the plain span below.
                        className={`mcal-item off ${it.clash ? 'clash' : ''} ${it.entry.requestStatus === 'awaiting' ? 'awaiting' : ''}`}
                        title={
                          `${it.name}${windowLabel(it.entry, d) ? ` · ${windowLabel(it.entry, d)}` : ''}${it.entry.note ? ` · ${it.entry.note}` : ''}` +
                          (reasonLabel(it.entry.reason) ? ` · ${reasonLabel(it.entry.reason)}` : '') +
                          (it.entry.requestStatus === 'awaiting' ? ' · request not yet submitted' : '') +
                          (it.clash ? ' — BOOKED ON THIS DAY as well' : '') +
                          ' — click to change'
                        }
                        onClick={(e) => { e.stopPropagation(); onEditTimeOff(it.entry) }}
                      >
                        {it.name}
                        {windowLabel(it.entry, d) && <span className="mcal-off-hours"> {windowLabel(it.entry, d)}</span>}
                      </button>
                    ) : (
                      <span
                        key={it.key}
                        className={`mcal-item off ${it.clash ? 'clash' : ''}`}
                        title={[windowLabel(it.entry, d), it.entry.note].filter(Boolean).join(' · ') || 'Time off'}
                      >
                        {it.name}
                        {windowLabel(it.entry, d) && <span className="mcal-off-hours"> {windowLabel(it.entry, d)}</span>}
                      </span>
                    )
                  ) : (
                    <span
                      key={it.key}
                      className="mcal-item on"
                      title={[it.name, ...it.jobs].join(NEWLINE)}
                    >
                      {it.name}
                    </span>
                  ),
                )}
                {/* Not a target of its own — the whole cell already opens the day, and a
                    link inside it only invited the question of what the other 95% did. */}
                {overflow > 0 && <span className="mcal-more">{overflow} more…</span>}
              </div>
            </div>
          )
        })}
      </div>

      {menu && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          <ContextMenuLabel>{formatDate(menu.date, { withYear: true })}</ContextMenuLabel>
          <ContextMenuItem
            icon="calendar"
            onClick={() => { onAddDay(menu.date); setMenu(null) }}
          >
            Add time off
          </ContextMenuItem>
        </ContextMenu>
      )}
    </div>
  )
}
